import { IonCol, IonIcon, IonItem, IonLabel, IonList, IonRow, IonText } from "@ionic/react";
import { checkmarkDoneOutline, documentTextOutline, receiptOutline, timeOutline } from "ionicons/icons";
import { UploadButton } from "./UploadButton";

interface DocumentProps {
  documentList: any[];
  expiration: number;
  id_doc?: number;
  userId?: string;
}

export const UserDocumentsList: React.FC<DocumentProps> = ({
                                                             documentList,
                                                             expiration,
                                                             id_doc,
                                                             userId,
                                                           }) => {
  const expirationDays = expiration;

  const convertDigitIn = (documents: any) => {
    const toDate = documents.toDate;
    const date1 = new Date(toDate);
    const date2 = new Date();
    const timeInMilisec = date1.getTime() - date2.getTime();
    const daysBetweenDates = Math.ceil(timeInMilisec / (1000 * 60 * 60 * 24));

    if (isNaN(daysBetweenDates)) return "Error en la fecha de vencimiento";
    if (daysBetweenDates < 0) return `Vencido hace: ${Math.abs(daysBetweenDates)} días`;
    if (daysBetweenDates === 0) return "Vence hoy";
    return `Vence en: ${daysBetweenDates} días`;
  };

  const expirationStatus = (documents: any) => {
    const toDate = documents.toDate;
    const date1 = new Date(toDate);
    const date2 = new Date();
    const daysBetweenDates = Math.ceil((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));

    if (daysBetweenDates > 0 && daysBetweenDates < expirationDays) return "pav";
    if (toDate != null && daysBetweenDates < 0) return "vencido";
    return undefined;
  };

  const okStatus = (documents: any) => {
    const toDate = documents.toDate;
    const date1 = new Date(toDate);
    const date2 = new Date();
    const daysBetweenDates = Math.ceil((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));

    const nextToexpire = daysBetweenDates > 0 && daysBetweenDates < expirationDays;
    const expired = toDate != null && daysBetweenDates < 0;
    const pendingSignature = documents.status?.id === 2;
    const compliancePending = documents.complianceStatus === "documento_subido";
    const complianceRejected = documents.complianceStatus === "devuelto";
    const complianceWaiting = documents.complianceStatus === "creacion";

    if (!nextToexpire && !expired && !pendingSignature && !compliancePending && !complianceRejected && !complianceWaiting) {
      return "ok";
    }
    return undefined;
  };

  const convertDate = (documents: any) => {
    const startDate = documents.fromDate;
    const toDate = documents.toDate;
    const date1 = new Date(toDate);
    const date2 = new Date(startDate);

    const formattedStart =
      ("0" + date2.getDate()).slice(-2) + "/" +
      ("0" + (date2.getMonth() + 1)).slice(-2) + "/" +
      date2.getFullYear();

    const formattedEnd =
      ("0" + date1.getDate()).slice(-2) + "/" +
      ("0" + (date1.getMonth() + 1)).slice(-2) + "/" +
      date1.getFullYear();

    return `${formattedStart} - ${formattedEnd}`;
  };

  return (
    <IonList>
      {documentList?.map((documents: any, index: number) => {
        // Ruta de navegación interna
        let routerLink: string | undefined = undefined;

        if (documents.complianceStatus === "devuelto" || documents.complianceStatus === "creacion") {
          routerLink = undefined; // Se maneja con el botón Upload
        } else if (documents.complianceId) {
          routerLink = `/app/supervision/acuerdos/${documents.complianceId}`;
        } else if (documents.id) {
          routerLink = `/app/supervision/acuerdos/${documents.id}`;
        }

        return (
          <IonItem key={index} routerLink={routerLink} lines="none">
            <IonLabel style={{ maxWidth: "91.5%" }}>
              <h3>{documents.title}</h3>

              {documents.fromDate === null && documents.toDate === null ? (
                <h3>Subido: {documents.createdAt}</h3>
              ) : (
                <IonRow style={{ justifyContent: "space-around" }}>
                  <IonCol sizeLg="3" sizeMd="12" sizeXs="12">
                    <IonText>Subido: {documents.createdAt}</IonText>
                  </IonCol>
                  <IonCol sizeLg="5" sizeMd="12" sizeXs="12">
                    <IonText>Vigencia: {convertDate(documents)}</IonText>
                  </IonCol>
                  <IonCol sizeLg="4" sizeMd="12" sizeXs="12">
                    <IonText>{convertDigitIn(documents)}</IonText>
                  </IonCol>
                </IonRow>
              )}
            </IonLabel>

            {/* Estado OK */}
            {okStatus(documents) && (
              <IonLabel style={{ textAlign: "center", maxWidth: "fit-content" }}>
                <IonIcon icon={checkmarkDoneOutline} color="success" style={{ fontSize: 20, marginBottom: 3 }} />
              </IonLabel>
            )}

            {/* Pendiente de firma */}
            {documents.status?.id === 2 && (
              <IonLabel style={{ textAlign: "center", maxWidth: "fit-content" }}>
                <IonIcon icon={documentTextOutline} style={{ color: "red", fontSize: 20, marginBottom: 3, marginLeft: 5 }} />
              </IonLabel>
            )}

            {/* Por vencer */}
            {expirationStatus(documents) === "pav" && (
              <IonLabel style={{ textAlign: "center", maxWidth: "fit-content" }}>
                <IonIcon icon={timeOutline} color="warning" style={{ fontSize: 20, marginBottom: 3, marginLeft: 5 }} />
              </IonLabel>
            )}

            {/* Vencido */}
            {expirationStatus(documents) === "vencido" && (
              <IonLabel style={{ textAlign: "center", maxWidth: "fit-content" }}>
                <IonIcon icon={timeOutline} style={{ color: "red", fontSize: 20, marginBottom: 3, marginLeft: 5 }} />
              </IonLabel>
            )}

            {/* Compliance: Devuelto o Creación → Botón de subir */}
            {(documents.complianceStatus === "devuelto" || documents.complianceStatus === "creacion") && (
              <>
                <p>
                  <UploadButton id_doc={id_doc} userId={userId} text="Ver detalle y subir" />
                </p>
                <IonLabel
                  style={{
                    textAlign: "center",
                    maxWidth: "fit-content",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IonIcon icon={receiptOutline} style={{ color: "red", fontSize: 20, marginBottom: 3, marginLeft: 5 }} />
                </IonLabel>
              </>
            )}

            {/* Compliance: Documento subido */}
            {documents.complianceStatus === "documento_subido" && (
              <IonLabel style={{ textAlign: "center", maxWidth: "fit-content" }}>
                <IonIcon icon={receiptOutline} style={{ color: "blue", fontSize: 20, marginBottom: 3, marginLeft: 5 }} />
              </IonLabel>
            )}
          </IonItem>
        );
      })}
    </IonList>
  );
};
